import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { Typography } from "components/common";

interface CallInfo {
  destination: string;
  callerIDNumber: string;
  answerState?: string;
  isHolding?: boolean;
  isParked?: boolean;
}

const ellipsisStyle = {
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

export const useInfoStatus = (info: CallInfo) => {
  const [infoText, setInfoText] = useState<JSX.Element | string>("");
  const [statusText, setStatusText] = useState<string>("");

  const ivrMenus = useSelector((state: RootState) => state.directory.ivr_menu.items ?? []);
  const ringGroups = useSelector((state: RootState) => state.directory.ring_group.items ?? []);
  const extensions = useSelector((state: RootState) => state.directory.extension.items ?? []);
  const callCenterData = useSelector((state: RootState) => state.directory.queue.items ?? []);

  useEffect(() => {
    const getInfoElement = (name: string, ext: string) => (
      <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
        {name} ({ext})
      </Typography>
    );

    const getCallInfo = (
      name: string,
      ext: string,
      action: "hold" | "park" | "talking" | "status",
      answerState?: string
    ) => {
      const label =
        action === "hold"
          ? "call on hold with"
          : action === "park"
          ? "call on park with"
          : action === "talking"
          ? answerState === "active"
            ? "Talking to"
            : `${answerState} to`
          : "";

      return (
        <Typography variant="small-text-3" style={ellipsisStyle}>
          {label}{" "}
          <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
            {name} ({ext})
          </Typography>
        </Typography>
      );
    };

    const matchByExtension = <T extends { extension?: string }>(list: T[], ext: string) =>
      list.find((item) => item.extension === ext);

    const { destination, callerIDNumber, isHolding, isParked, answerState } = info;

    const matchedIVR = matchByExtension(ivrMenus, destination);
    const matchedRing = matchByExtension(ringGroups, destination);
    const matchedExt = matchByExtension(extensions, destination);
    const matchedCC = matchByExtension(callCenterData, destination);

    if (matchedIVR) {
      setInfoText(getCallInfo(matchedIVR.name, destination, isHolding ? "hold" : isParked ? "park" : "status"));
    } else if (matchedRing) {
      setInfoText(getCallInfo(matchedRing.name, destination, isHolding ? "hold" : isParked ? "park" : "status"));
    } else if (matchedExt) {
      setInfoText(getCallInfo(matchedExt.name, destination, isHolding ? "hold" : isParked ? "park" : "talking", answerState));
    } else if (matchedCC) {
      setInfoText(
        <Typography variant="small-text-3" style={ellipsisStyle}>
          {matchedCC.name}{" "}
          <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
            ({destination})
          </Typography>
        </Typography>
      );
    } else {
      const fallbackText =
        isHolding || isParked
          ? `call on ${isHolding ? "hold" : "park"} with`
          : answerState === "active"
          ? "Talking to"
          : `${answerState} by`;

      setInfoText(
        <Typography variant="small-text-3" style={ellipsisStyle}>
          {fallbackText}{" "}
          <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
            {destination}
          </Typography>
        </Typography>
      );
    }

    const callerExt = callerIDNumber;
    const isOutbound =
      matchByExtension(ivrMenus, callerExt) ||
      matchByExtension(ringGroups, callerExt) ||
      matchByExtension(extensions, callerExt);

    setStatusText(isOutbound ? "Outbound" : "Inbound");
  }, [info, ivrMenus, ringGroups, extensions, callCenterData]);

  return { infoText, statusText };
};
